import { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack,
  Box, Alert, Table, TableBody, TableCell, TableHead, TableRow, Chip, alpha, useTheme,
  LinearProgress,
} from '@mui/material';
import { UploadFile, Download, CloudUpload } from '@mui/icons-material';
import type { MasterSchema } from '../../constants/masters';
import { downloadMasterTemplate, parseMasterCsv } from '../../utils/masterCsv';
import type { MasterCsvRow } from '../../utils/masterCsv';

interface MasterImportDialogProps {
  open: boolean;
  onClose: () => void;
  masterType: string;
  masterLabel: string;
  schema?: MasterSchema | null;
  loading?: boolean;
  onImport: (rows: MasterCsvRow[]) => void;
  result?: { created: number; updated: number; failed: { code: string; error: string; row?: number }[] } | null;
}

const IMPORT_GUIDANCE: Record<string, string[]> = {
  states: [
    'Country — required ISO code of a country already in the Countries master (e.g. IN).',
    'Name — required, unique within the country.',
    'Code — optional. Leave empty to auto-generate (ISO 3166-2, e.g. IN-KA); if given it must start with the country code. Existing codes are updated, but their country cannot change.',
    'Sort Order — optional, unique within the country; empty uses the next available number.',
    'Rows with duplicate codes or names in the same file, unknown countries or malformed codes are rejected with the row number.',
  ],
  cities: [
    'Country — required ISO code (e.g. IN). State — required state code under that country (e.g. TN or IN-TN); the state must exist and be active.',
    'Name — required, unique within the state. A city that already exists in the state is updated instead of duplicated.',
    'Code — leave empty; it is auto-generated (e.g. IN_TN_CHENNAI).',
    'Sort Order — optional, unique within the state; empty uses the next available number.',
    'Rows with missing fields, unknown or inactive states, or duplicate names in the same file are rejected with the row number.',
  ],
  currencies: [
    'Code — required ISO 4217 code (e.g. EUR). Existing codes are updated; new codes are added.',
    'ISO Numeric Code — optional; filled from the code and must match ISO 4217 (e.g. 978).',
    'Symbol (1–6 characters, no digits) and Decimal Places (0–4) are required.',
    'Exchange rate = units of this currency per 1 base currency. Auto update needs Rate Source "api" and a Frankfurter-supported currency.',
    'Separators must differ; cash rounding must be a coin/note value (0.05, 0.10, 1…); maximum payment must be ≥ minimum.',
    'Sort Order — optional and unique; empty uses the next available number.',
  ],
  timezones: [
    'IANA Timezone ID — required, Region/City format (e.g. Asia/Kolkata, America/New_York). Old aliases such as Asia/Calcutta are rejected with the current name.',
    'Existing IANA IDs are updated (name, description, sort order); new ones are added as Active.',
    'Code, UTC Offset, DST Supported and Abbreviation are derived from the IANA ID. If you include them they must match — e.g. America/New_York is UTC-05:00 / -04:00 with DST.',
    'Name — optional and unique; empty uses the standard name (e.g. "Eastern Time – New York").',
    'Sort Order — optional, whole number 1–9999 and unique; empty uses the next available number.',
  ],
  banks: [
    'Name — required, letters, spaces and & . \' ( ) - only, unique within the country. Country — ISO code from the Countries master (default IN).',
    'IFSC Prefix — required for Indian banks: exactly 4 letters (e.g. HDFC); must be empty for other countries. Code is generated automatically (the IFSC prefix for Indian banks, uppercase).',
    'SWIFT Code — 8 or 11 characters (e.g. HDFCINBB) whose country part matches the bank country. Set International Transfers to false for domestic-only banks; an empty SWIFT means domestic only.',
    'Existing codes are updated; duplicates by code, name or IFSC prefix — in the file or already in the master — are rejected with the row number.',
    'Sort Order — optional, whole number and unique; empty uses the next available number.',
  ],
  payment_methods: [
    'Name — required, letters, spaces, apostrophes and hyphens only. Code is generated from the name (e.g. Net Banking → net_banking); existing codes are updated, never renamed.',
    'Gateway — required. Cash methods use cash, wallet methods use wallet, bank transfer uses bank_transfer; all others need an online gateway (razorpay, stripe, paypal, phonepe, cashfree…).',
    'Fee Percent — optional, 0–20 with up to 2 decimals. Fee Basis — required when the fee is above 0: platform, gateway or both (cash / wallet / bank transfer allow platform only).',
    'Methods whose gateway is not enabled with credentials in Payment Gateways are imported as Inactive.',
    'Sort Order — optional, whole number and unique; empty uses the next available number. Duplicate codes in the same file are rejected with the reason.',
  ],
  payout_cycles: [
    'Name — required, letters, spaces, apostrophes and hyphens only. Code is generated from the name (e.g. Bi Weekly → bi_weekly); existing codes are updated, never renamed.',
    'Days — required, whole number 1–90 and different for every cycle (e.g. 1 daily, 7 weekly, 30 monthly).',
    'Sort Order — optional, whole number and unique; empty uses the next available number. The first active cycle by sort order is the default for partners who have not chosen one.',
  ],
  address_fields: [
    'Geo Level — required: country, state, city, district, pincode, line1, line2, landmark or custom. Each level except custom can appear only once.',
    'Field Key — fixed for geo levels (e.g. pincode); for custom fields lowercase letters, digits and underscores, 2–40 characters (e.g. flat_number). Code always equals the Field Key.',
    'Existing Field Keys are updated; their Geo Level and Key cannot change. New keys are added as Active.',
    'Field Type / Data Source — optional when the geo level allows only one (Country/State/City are Dropdown from Master Data, Pincode is Postal code). Other values must match the allowed list.',
    'Name — required, letters only, unique. Required — true/false; Country, State and City are always required.',
    'Hierarchy — Country → State → City → District rows must keep that display order, and a child cannot be active or required without its parent.',
    'Sort Order — optional, whole number 1–9999 and unique; empty uses the next available number. Duplicate keys in the same file are rejected with the row number.',
  ],
  languages: [
    'Code — required ISO 639-1 code (e.g. hi). Existing codes are updated; new codes are added as Active.',
    'Locale — BCP 47 starting with the code (e.g. hi-IN, zh-Hant-TW); empty uses the standard locale for the language.',
    'Native Name — the language written in itself (e.g. हिन्दी); empty uses the ISO native name.',
    'Text direction and translation status are set by the system and ignored in the file.',
    'Sort Order — optional, whole number 1–999 and unique; empty uses the next available number.',
  ],
};

export default function MasterImportDialog({
  open,
  onClose,
  masterType,
  masterLabel,
  schema,
  loading,
  onImport,
  result,
}: MasterImportDialogProps) {
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<MasterCsvRow[]>([]);
  const [parseError, setParseError] = useState('');
  const guidance = IMPORT_GUIDANCE[masterType];
  const showCountry = !!schema?.geoFields?.includes('country');

  const handleFile = (file: File) => {
    setParseError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseMasterCsv(String(reader.result || ''), schema);
        if (!rows.length) {
          setParseError('No valid rows found. Check CSV headers match the template.');
          setPreview([]);
          return;
        }
        setPreview(rows);
      } catch {
        setParseError('Could not parse CSV file.');
        setPreview([]);
      }
    };
    reader.readAsText(file);
  };

  const handleClose = () => {
    setPreview([]);
    setParseError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800}>Bulk Import — {masterLabel}</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a CSV file. Existing codes will be updated; new codes will be created.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => downloadMasterTemplate(masterType, schema)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Download Template
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => fileRef.current?.click()}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Choose CSV File
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
        </Stack>

        {guidance && !result && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>Import rules</Typography>
            {guidance.map((line) => (
              <Typography key={line} variant="body2">• {line}</Typography>
            ))}
          </Alert>
        )}

        {parseError && <Alert severity="error" sx={{ mb: 2 }}>{parseError}</Alert>}

        {result && (
          <Alert severity={result.failed.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Imported: {result.created} created, {result.updated} updated
            {result.failed.length > 0 && `, ${result.failed.length} failed`}
          </Alert>
        )}

        {result && result.failed.length > 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.error.main, 0.06), borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={700}>Rejected rows ({result.failed.length})</Typography>
            </Box>
            <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 72 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Record</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.failed.map((f, i) => (
                    <TableRow key={`${f.row ?? i}-${f.code}`}>
                      <TableCell>{f.row ?? '—'}</TableCell>
                      <TableCell><Typography variant="caption" fontFamily="monospace">{f.code}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="error.main">{f.error}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {preview.length > 0 && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: alpha(theme.palette.primary.main, 0.06), borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <UploadFile fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Preview ({preview.length} rows)
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    {showCountry && <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.slice(0, 50).map((row, i) => (
                    <TableRow key={`${row.code}-${i}`}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {row.code || (masterType === 'states' ? 'auto' : '')}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      {showCountry && <TableCell>{row.country || '—'}</TableCell>}
                      <TableCell>
                        <Chip size="small" label={row.isActive !== false ? 'Active' : 'Off'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {preview.length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1.5, display: 'block' }}>
                Showing first 50 of {preview.length} rows
              </Typography>
            )}
          </Box>
        )}

        {!preview.length && !parseError && (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <CloudUpload sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Download the template, fill in your data, then upload the CSV
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!preview.length || loading}
          onClick={() => onImport(preview)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Import {preview.length ? `${preview.length} Records` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
